// AI Agents · Activity & reports.
//
// Top-level view (palette tab 'ai_activity'). Surfaces:
//   - Window picker (24h / 7d / 30d / all)
//   - Totals strip (runs, cost, successes, failures, agents active)
//   - Per-agent stat cards (incl. AI Setup Agent from agent_tool_runs)
//   - Unified activity feed with per-row drill-down modal
//
// Backed by GET /api/ai-agents/usage-report.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Bot, MessageSquare, Phone, Mail, Calendar, AlertTriangle, CheckCircle2, X, RefreshCcw, ShieldCheck, Zap, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, { headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const fmtUSD = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const ACTION_ICON = {
  sms_sent:           MessageSquare,
  sms_inbound_parsed: MessageSquare,
  voice_call:         Phone,
  voicemail_left:     Phone,
  email_sent:         Mail,
  appointment_booked: Calendar,
  lapse_saved:        ShieldCheck,
  uw_chase:           FileText,
  digest_published:   FileText,
  no_op:              Zap,
  error:              AlertTriangle,
  // SetupAgent tools
  'a2p-submit':       ShieldCheck,
  'a2p-status':       Bot,
  'a2p-review':       ShieldCheck,
  'provision-number': Phone,
};

// Operator-facing agent names — kept in sync with the AI_AGENT_DISPLAY_NAMES
// map in CommissionTracker.jsx. Renamed 2026-06-26 from product-y labels
// ("Appointment Setter") to brain names (NOVA / RETAIN / SCOUT / VOX) so
// the activity view, settings panel, and FAB pills all speak the same
// language.
const AGENT_TYPE_LABEL = {
  setter:          'NOVA',
  policy_manager:  'RETAIN',
  agency_manager:  'SCOUT',
  dialer:          'VOX',
  atlas:           'ATLAS',
  custom:          'Custom agent',
  setup:           'Setup Agent',
};

const WINDOWS = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d',  label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
];

export default function AiAgentsActivityView({ currentUser, showToast = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [window, setWindow]   = React.useState('7d');
  const [agentFilter, setAgentFilter] = React.useState('all'); // 'all' | agent_id | 'setup'
  const [drilldown, setDrilldown]     = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setData(await apiFetch(`/api/ai-agents/usage-report?window=${window}`)); }
    catch (err) { showToast(`Load failed: ${err.message}`); }
    finally { setLoading(false); }
  }, [window, showToast]);
  React.useEffect(() => { load(); }, [load]);

  // Live updates — when an agent run lands, refresh the rollup.
  React.useEffect(() => {
    if (!currentUser?.id) return;
    const ch = supabase.channel(`ai_agent_activity_${currentUser.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'ai_agent_runs', filter: `owner_id=eq.${currentUser.id}` }, load)
      .on('postgres_changes', { event: '*',      schema: 'public', table: 'agent_tool_runs', filter: `owner_id=eq.${currentUser.id}` }, load)
      .subscribe();
    return () => { try { supabase.removeChannel(ch); } catch {} };
  }, [currentUser?.id, load]);

  if (loading && !data) return <div className="text-sm text-slate-500 p-4">Loading AI activity…</div>;
  if (!data) return null;

  const filteredFeed = agentFilter === 'all'
    ? data.activity
    : agentFilter === 'setup'
      ? data.activity.filter(r => r.kind === 'setup_run')
      : data.activity.filter(r => r.agent_id === agentFilter);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-violet-900 text-white rounded-xl p-5">
        <div className="text-violet-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">AI Agents · Activity & Reports</div>
        <h1 className="text-xl font-bold mt-1">What your AI is doing for you</h1>
        <p className="text-slate-300 text-sm mt-2">Every action your AI agents took — texts sent, calls placed, appointments booked, A2P registrations submitted — with the receipts. Click any row to see the full report.</p>
      </div>

      {/* WINDOW PICKER + REFRESH */}
      <div className="flex items-center gap-2 flex-wrap">
        {WINDOWS.map(w => (
          <button key={w.key} onClick={() => setWindow(w.key)}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 ${window === w.key ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>
            {w.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-slate-400 hover:text-slate-700"><RefreshCcw size={14}/></button>
      </div>

      {/* TOTALS STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Runs"           value={data.totals.runs}                tone="slate"/>
        <Stat label="Agents active"  value={data.totals.agents_active}       tone="violet"/>
        <Stat label="Successes"      value={data.totals.successes}           tone="emerald"/>
        <Stat label="Failures"       value={data.totals.failures}            tone="rose"/>
        <Stat label="Cost"           value={fmtUSD(data.totals.cost_cents)}  tone="sky"/>
      </div>

      {/* PER-AGENT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.agents.map(a => (
          <AgentCard key={a.id} agent={a} active={agentFilter === a.id} onClick={() => setAgentFilter(agentFilter === a.id ? 'all' : a.id)}/>
        ))}
        <SetupAgentCard data={data.setup_agent} active={agentFilter === 'setup'} onClick={() => setAgentFilter(agentFilter === 'setup' ? 'all' : 'setup')}/>
      </div>

      {/* ACTIVITY FEED */}
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 flex-wrap gap-2">
          <h3 className="font-bold text-slate-900">Activity {agentFilter !== 'all' && <span className="text-xs text-slate-500 font-normal">· filtered</span>}</h3>
          <div className="text-xs text-slate-500">{filteredFeed.length} row{filteredFeed.length === 1 ? '' : 's'}</div>
        </div>
        {filteredFeed.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10">No activity in this window.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredFeed.map(row => <ActivityRow key={`${row.kind}-${row.id}`} row={row} onClick={() => setDrilldown(row)}/>)}
          </div>
        )}
      </div>

      {drilldown && <DrilldownModal row={drilldown} onClose={() => setDrilldown(null)}/>}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const map = {
    slate:   'bg-white border-slate-200 text-slate-900',
    violet:  'bg-violet-50 border-violet-200 text-violet-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose:    'bg-rose-50 border-rose-200 text-rose-900',
    sky:     'bg-sky-50 border-sky-200 text-sky-900',
  };
  return (
    <div className={`border rounded-lg p-3 ${map[tone] || map.slate}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-black mt-0.5">{value}</div>
    </div>
  );
}

function AgentCard({ agent, active, onClick }) {
  const successRate = agent.runs > 0 ? Math.round((agent.successes / agent.runs) * 100) : null;
  return (
    <button onClick={onClick}
      className={`text-left bg-white border-2 rounded-xl p-4 hover:border-violet-400 transition-colors ${active ? 'border-violet-600 ring-2 ring-violet-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-slate-900 truncate">{agent.name}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-0.5">{AGENT_TYPE_LABEL[agent.agent_type] || agent.agent_type}</div>
        </div>
        {!agent.enabled && <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5">Off</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs">
        <div><div className="text-slate-500 text-[10px] uppercase tracking-wider">Runs</div><div className="font-bold text-slate-900">{agent.runs}</div></div>
        <div><div className="text-slate-500 text-[10px] uppercase tracking-wider">Cost</div><div className="font-bold text-slate-900">{fmtUSD(agent.cost_cents)}</div></div>
        <div><div className="text-slate-500 text-[10px] uppercase tracking-wider">Success</div><div className="font-bold text-slate-900">{successRate == null ? '—' : `${successRate}%`}</div></div>
      </div>
      {agent.top_actions?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {agent.top_actions.slice(0, 3).map(a => (
            <span key={a.action} className="text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{a.action}: {a.count}</span>
          ))}
        </div>
      )}
      <div className="text-[10px] text-slate-400 mt-2">Last run {fmtAgo(agent.last_run_at)}</div>
    </button>
  );
}

function SetupAgentCard({ data, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`text-left bg-white border-2 rounded-xl p-4 hover:border-violet-400 transition-colors ${active ? 'border-violet-600 ring-2 ring-violet-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-slate-900">AI Setup Agent</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-0.5">A2P · Number provisioning</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs">
        <div><div className="text-slate-500 text-[10px] uppercase tracking-wider">Runs</div><div className="font-bold text-slate-900">{data.runs}</div></div>
        <div><div className="text-slate-500 text-[10px] uppercase tracking-wider">Executed</div><div className="font-bold text-emerald-700">{data.executed}</div></div>
        <div><div className="text-slate-500 text-[10px] uppercase tracking-wider">Pending</div><div className="font-bold text-amber-700">{data.pending}</div></div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {Object.entries(data.tools_used).map(([k, v]) => (
          <span key={k} className="text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{k}: {v}</span>
        ))}
      </div>
      <div className="text-[10px] text-slate-400 mt-2">Last run {fmtAgo(data.last_run_at)}</div>
    </button>
  );
}

function ActivityRow({ row, onClick }) {
  const Icon = ACTION_ICON[row.action] || Bot;
  const tone = outcomeTone(row);
  return (
    <button onClick={onClick} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-start gap-3">
      <div className={`mt-0.5 p-1.5 rounded-md ${tone.bg}`}>
        <Icon size={14} className={tone.icon}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm truncate">{row.agent_name}</span>
          <span className="text-xs text-slate-500">{row.action}</span>
          {row.outcome && <span className={`text-[10px] font-bold uppercase tracking-wider border rounded px-1.5 py-0.5 ${tone.badge}`}>{row.outcome}</span>}
        </div>
        {row.message && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{row.message}</div>}
      </div>
      <div className="text-right flex-shrink-0">
        {row.cost_cents > 0 && <div className="text-xs font-bold text-slate-700">{fmtUSD(row.cost_cents)}</div>}
        <div className="text-[10px] text-slate-400">{fmtAgo(row.created_at)}</div>
      </div>
    </button>
  );
}

function outcomeTone(row) {
  const out = row.outcome;
  if (out === 'success' || out === 'executed')
    return { bg: 'bg-emerald-100', icon: 'text-emerald-700', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (out === 'error' || out === 'failed' || out === 'rejected')
    return { bg: 'bg-rose-100',    icon: 'text-rose-700',    badge: 'bg-rose-50 text-rose-700 border-rose-200' };
  if (out === 'pending_approval' || out === 'approved')
    return { bg: 'bg-amber-100',   icon: 'text-amber-700',   badge: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { bg: 'bg-slate-100', icon: 'text-slate-600', badge: 'bg-slate-50 text-slate-600 border-slate-200' };
}

function DrilldownModal({ row, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900">{row.agent_name} · {row.action}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{new Date(row.created_at).toLocaleString()}{row.outcome && ` · ${row.outcome}`}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          {row.message && (
            <Section title="Message">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-800 whitespace-pre-wrap">{row.message}</div>
            </Section>
          )}
          {row.cost_cents > 0 && (
            <Section title="Cost"><div className="text-sm font-bold">{fmtUSD(row.cost_cents)}</div></Section>
          )}
          {row.drilldown?.preview && (
            <Section title="What the agent proposed">
              <JsonBlock value={row.drilldown.preview}/>
            </Section>
          )}
          {row.drilldown?.result && (
            <Section title="Result">
              <JsonBlock value={row.drilldown.result}/>
            </Section>
          )}
          {row.drilldown?.error && (
            <Section title="Error">
              <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800">{row.drilldown.error}</div>
            </Section>
          )}
          {row.metadata && Object.keys(row.metadata).length > 0 && (
            <Section title="Metadata">
              <JsonBlock value={row.metadata}/>
            </Section>
          )}
          {row.lead_id && (
            <Section title="Lead">
              <div className="text-xs font-mono text-slate-700">{row.lead_id}</div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{title}</div>
      {children}
    </div>
  );
}

function JsonBlock({ value }) {
  return (
    <pre className="bg-slate-900 text-emerald-300 text-[11px] rounded-md p-3 overflow-x-auto font-mono whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
  );
}
